import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { toLocalTime } from '../utils/time';
import StatusPill from './StatusPill';
import ConfirmModal from './ConfirmModal';
import ClientCapsules from './ClientCapsules';

// Notes longer than this get truncated with a "Read more" tap target instead
// of rendering in full — keeps card heights predictable regardless of how
// much someone writes.
const NOTE_TRUNCATE_THRESHOLD = 140;

// Cards show at most this many sessions inline — covers the common case (one
// session, or one plus a quick break) without truncating. Anything beyond
// that collapses behind a "Read more" that opens the full day's sessions in
// a modal, same reasoning as the individual-note truncation above.
const SESSION_VISIBLE_COUNT = 2;

// Shared card grid for both the Admin dashboard (full detail: status +
// every session for the selected CST day) and the VA team-status view
// (lighter: just who's in/out).
//
// `displayTimezone` overrides which zone times are shown in — defaults to
// the viewer's own if not given, but callers pass the CST/local toggle's
// effective timezone so this doesn't need to know about that setting itself.
export default function TeamGrid({
  teamRows,
  showNotes = true,
  showLastAction = true,
  showProfileOnClick = false,
  displayTimezone,
}) {
  const { currentUser } = useApp();
  const tz = displayTimezone || currentUser.timezone;
  // Backs both the "+N more" (hidden sessions) and per-session "Read more"
  // (a visible session with a long note) triggers — both just open the same
  // modal with whichever sessions need the full, untruncated In/Out view.
  const [openDay, setOpenDay] = useState(null); // { user, sessions } | null
  const [openProfile, setOpenProfile] = useState(null); // user | null

  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {teamRows.map(({ user, latest, displayStatus, daySessions }) => {
          const visibleSessions = daySessions ? daySessions.slice(0, SESSION_VISIBLE_COUNT) : [];
          const hiddenCount = (daySessions ? daySessions.length : 0) - visibleSessions.length;

          return (
            <div key={user.uuid} className="bg-card border border-border rounded-xl p-3 sm:p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted">
                    {user.isAdmin ? 'Admin' : 'VA'}
                  </p>
                  {showProfileOnClick ? (
                    <button
                      onClick={() => setOpenProfile(user)}
                      className="text-sm sm:text-base font-medium text-ink hover:text-gold hover:underline transition-colors text-left"
                    >
                      {user.name}
                    </button>
                  ) : (
                    <span className="text-sm sm:text-base font-medium text-ink">{user.name}</span>
                  )}
                </div>
                <StatusPill status={displayStatus} compact />
              </div>
              {showLastAction && (
                <p className="mt-2 text-[11px] sm:text-xs text-muted">
                  {latest ? `Last action: ${toLocalTime(latest.logtime_utc, tz)}` : 'No activity yet'}
                </p>
              )}
              {showNotes && daySessions && daySessions.length > 0 && (
                <div className="mt-2 border-t border-border pt-2 space-y-2">
                  {visibleSessions.map((s, i) => {
                    const isLongSession =
                      (s.in?.notes && s.in.notes.length > NOTE_TRUNCATE_THRESHOLD) ||
                      (s.out?.notes && s.out.notes.length > NOTE_TRUNCATE_THRESHOLD);
                    return (
                      <div key={s.in?.log_id || s.out?.log_id || i} className="space-y-1.5">
                        {s.in && <SessionEntry entry={s.in} tz={tz} />}
                        {s.out && <SessionEntry entry={s.out} tz={tz} />}
                        {isLongSession && (
                          <button
                            onClick={() => setOpenDay({ user, sessions: [s] })}
                            className="text-[11px] font-semibold text-bronze hover:text-ink transition-colors"
                          >
                            Read more
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {hiddenCount > 0 && (
                    <button
                      onClick={() => setOpenDay({ user, sessions: daySessions })}
                      className="text-[11px] font-semibold text-bronze hover:text-ink transition-colors"
                    >
                      +{hiddenCount} more — Read more
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ConfirmModal
        open={Boolean(openDay)}
        title={openDay ? openDay.user.name : ''}
        onCancel={() => setOpenDay(null)}
        footer={<CloseButton onClick={() => setOpenDay(null)} />}
      >
        {openDay && (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-3 scroll-thin">
            {openDay.sessions.map((s, i) => (
              <div key={s.in?.log_id || s.out?.log_id || i} className="space-y-1.5">
                {s.in && <FullSessionEntry entry={s.in} tz={tz} />}
                {s.out && <FullSessionEntry entry={s.out} tz={tz} />}
              </div>
            ))}
          </div>
        )}
      </ConfirmModal>

      <ConfirmModal
        open={Boolean(openProfile)}
        title={openProfile ? openProfile.name : ''}
        onCancel={() => setOpenProfile(null)}
        footer={<CloseButton onClick={() => setOpenProfile(null)} />}
      >
        {openProfile && <ProfileDetails user={openProfile} />}
      </ConfirmModal>
    </>
  );
}

// Read-only — just the fields already sitting in the Users data this whole
// grid is built from, no separate fetch needed. Clients is the one
// exception — that's a join against ClientStaff/Clients, both already
// loaded in AppContext alongside everything else.
function ProfileDetails({ user }) {
  const { clientStaff, clients } = useApp();
  const rows = [
    ['Role', user.role],
    ['Email', user.gmail],
    ['Contact number', user.contact_number],
    ['Address', user.address],
    ['Birthday', user.birthday],
    ['Date of hire', user.date_of_hire],
    ['Timezone', user.timezone],
  ].filter(([, value]) => value);

  return (
    <div className="space-y-3">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted">
        {user.isAdmin ? 'Admin' : 'VA'}
      </p>
      {rows.map(([label, value]) => (
        <div key={label}>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted">{label}</p>
          <p className="text-sm text-ink whitespace-pre-wrap">{value}</p>
        </div>
      ))}
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted mb-1.5">Clients</p>
        <ClientCapsules uuid={user.uuid} clientStaff={clientStaff} clients={clients} />
      </div>
    </div>
  );
}

function CloseButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full py-2.5 rounded-xl font-medium border border-border text-ink hover:bg-card-tint transition-colors"
    >
      Close
    </button>
  );
}

// Compact card view — notes are visually clipped to 2 lines (CSS, not a
// character count) rather than carrying their own "Read more" affordance.
// The session as a whole gets one "Read more" (see the parent map above)
// when either its In or Out note runs long, rather than each entry getting
// its own — two separate "either/or" buttons per session was the confusing
// part being fixed here.
function SessionEntry({ entry, tz }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 min-w-0">
          <span
            className={`text-[10px] font-semibold uppercase tracking-wide ${
              entry.isIn ? 'text-success-tx' : 'text-out-tx'
            }`}
          >
            {entry.isIn ? 'In' : 'Out'}
          </span>
          {entry.is_manual && (
            <span className="text-[10px] italic text-muted truncate">(manually logged)</span>
          )}
          {entry.notes_edited && (
            <span className="text-[10px] italic text-muted truncate">(note edited)</span>
          )}
        </span>
        <span className="text-[10px] text-muted flex-shrink-0 whitespace-nowrap">
          {toLocalTime(entry.logtime_utc, tz)}
        </span>
      </div>
      {entry.notes && <p className="text-xs sm:text-sm text-ink mt-0.5 line-clamp-2">{entry.notes}</p>}
    </div>
  );
}

// Same layout as SessionEntry but for the "full day" modal — notes render in
// full since the whole point of that view is to show everything.
function FullSessionEntry({ entry, tz }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 min-w-0">
          <span
            className={`text-[10px] font-semibold uppercase tracking-wide ${
              entry.isIn ? 'text-success-tx' : 'text-out-tx'
            }`}
          >
            {entry.isIn ? 'In' : 'Out'}
          </span>
          {entry.is_manual && (
            <span className="text-[10px] italic text-muted truncate">(manually logged)</span>
          )}
          {entry.notes_edited && (
            <span className="text-[10px] italic text-muted truncate">(note edited)</span>
          )}
        </span>
        <span className="text-[10px] text-muted flex-shrink-0 whitespace-nowrap">
          {toLocalTime(entry.logtime_utc, tz)}
        </span>
      </div>
      {entry.notes && <p className="text-xs sm:text-sm text-ink mt-0.5 whitespace-pre-wrap">{entry.notes}</p>}
    </div>
  );
}
